import "@fontsource/qwigley/400.css";
import { useICDCodesList } from "components/Dashboard/DoctorDashboard/contexts/icd-codes-list-context";
import AutoFocusAlert from "components/auto-focus-alert";
import NavBrand from "components/nav-brand/NavBrand";
import {
  DISABLE_OLD_TEMPLATE_CONTENT,
  INCLUDE_INITIALS_IN_NOTE_FILE_NAMES,
  ENABLE_NEW_TEMPLATE_CONTENT,
} from "constants/featureFlags";
import { useUser } from "contexts/UserContext";
import type { OrchidDriveFile } from "contexts/orchid-drive/types";
import messages from "messages";
import moment from "moment-timezone";
import TemplateContent from "notes-and-forms-v2/components/template-content/TemplateContent";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container } from "react-bootstrap";
import getClasses from "utils/getClasses";
import printPage from "utils/printPage";
import { BLOCK_TYPE_MODES } from "../../constants/blockTypeModes";
import withAddendums from "../../hocs/withAddendums";
import withBlockAttachments from "../../hocs/withBlockAttachments";
import layout from "../../layout.module.scss";
import ClientServiceAndDiagnosis from "../../scenes/note-fill-in/components/client-service-and-diagnosis";
import type { TNotesType } from "../../types";
import FillInContent from "../fill-in-content/FillInContent";
import PrintFooter from "./PrintFooter";
import PrintHeader from "./PrintHeader";
import styles from "./PrintLayout.module.scss";
import downloadElementAsPdf from "./downloadElementAsPdf";

export type PrintLayoutMetaData = Pick<
  NoteMetaData,
  "patient" | "appointment" | "group_session" | "orchid_pro"
> & {
  note_json_schema?: JSONSchema;
  note_ui_schema?: UISchema;
  form_json_schema?: JSONSchema;
  form_ui_schema?: UISchema;
  sign_timestamp?: string;
  schema_v2: PersistedNoteOrFormTemplate;
};

interface PrintLayoutProps {
  error?: Error;
  type: TNotesType;
  metaData?: PrintLayoutMetaData;
  data?: Record<string, any>;
  addendums?: ParsedNoteAddendum[];
  /**
   * attachments by each block id
   */
  attachments?: Record<string, OrchidDriveFile[]>;
  noteCodes?: NoteCodes;
  /**
   * action to take on element is loaded
   */
  action?: "download" | "print";
  templateContentOptions?: TemplateContentOptions;
  /**
   * callback fn on action complete
   */
  onAction?: () => void;
  clientName?: string;
}

const PrintLayout = ({
  error,
  metaData,
  data,
  addendums,
  attachments,
  type,
  noteCodes,
  action,
  templateContentOptions,
  onAction,
  clientName,
}: PrintLayoutProps) => {
  const { busy } = useICDCodesList();

  const rootElementRef = useRef<HTMLDivElement | null>(null);

  const [hasPeformedAction, setHasPerformedAction] = useState(false);

  const { defaultUserTimezone } = useUser();

  const displayedTitle = useMemo(() => {
    if (!metaData) {
      return "";
    }

    const title = metaData[`${type}_json_schema`]?.title;

    if (!INCLUDE_INITIALS_IN_NOTE_FILE_NAMES) {
      return title;
    }

    const initials = [metaData.patient?.first_name, metaData.patient?.last_name]
      .map((name) => name?.charAt(0).toUpperCase())
      .join("");
    const date = metaData.appointment
      ? moment
          .tz(metaData.appointment.appointment_start, defaultUserTimezone)
          .format("YYYY-MM-DD")
      : "";

    return [initials, title, date].join(" ").replace(/\s/g, "_");
  }, [metaData, type, defaultUserTimezone]);

  useEffect(() => {
    if (!onAction || action !== "print") {
      return;
    }

    const windowPrintMediaListener = (mediaQuery: any) => {
      if (!mediaQuery.matches) {
        onAction();
      }
    };

    window.addEventListener("onafterprint", onAction);

    // add as a cross-browser compatibility
    if (window.matchMedia) {
      window.matchMedia("print").addListener(windowPrintMediaListener);
    }

    return () => {
      window.removeEventListener("onafterprint", onAction);

      // add as a cross-browser compatibility
      if (window.matchMedia) {
        window.matchMedia("print").removeListener(windowPrintMediaListener);
      }
    };
  }, [action, onAction]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (busy || !action || hasPeformedAction) {
        return;
      }

      if (action === "print") {
        setHasPerformedAction(true);
        printPage();
        return;
      }

      if (action === "download" && rootElementRef.current) {
        setHasPerformedAction(true);
        downloadElementAsPdf(
          rootElementRef.current,
          `${displayedTitle || "print"}.pdf`,
          onAction,
        );
      }
    });
  }, [action, busy, onAction, hasPeformedAction, displayedTitle]);

  const wrapped = useCallback(
    (children: JSX.Element) =>
      withBlockAttachments(
        type,
        withAddendums(type, children, {
          canAdd: false,
          addendums: addendums || [],
        }),
        {
          canChange: false,
          attachments: attachments || {},
        },
      ),
    [type, addendums, attachments],
  );

  return (
    <div
      ref={rootElementRef}
      className={getClasses(action && styles["print-layout"])}
    >
      <div
        className={getClasses(
          layout["layout__header"],
          styles["print-layout__header--pdf"],
        )}
      >
        <NavBrand />
        <div
          className={getClasses(
            layout["layout__actions"],
            styles["print-layout__actions"],
          )}
        >
          <Button
            variant="primary"
            size="lg"
            className={layout["layout__btn"]}
            onClick={() => printPage()}
          >
            {messages.print}
          </Button>
        </div>
      </div>
      <Container
        fluid
        className={getClasses(
          layout["layout__content"],
          styles["print-layout__body"],
        )}
      >
        {error && <AutoFocusAlert variant="danger" error={error} />}
        {metaData && (
          <PrintHeader metaData={metaData} clientName={clientName} />
        )}
        <hr className={styles["print-layout__hr"]} />
        <h3 className={styles["print-layout__title"]}>{displayedTitle}</h3>

        {ENABLE_NEW_TEMPLATE_CONTENT &&
          wrapped(
            <TemplateContent
              templateElements={metaData?.schema_v2?.elements ?? []}
              templateData={data ?? {}}
              isPrintMode={true}
              isDownloadMode={action === "download"}
              options={templateContentOptions}
            />,
          )}

        {metaData?.[`${type}_json_schema`] &&
          metaData?.[`${type}_ui_schema`] &&
          !DISABLE_OLD_TEMPLATE_CONTENT &&
          wrapped(
            <FillInContent
              disabled={true}
              jsonSchema={metaData[`${type}_json_schema`]}
              uiSchema={metaData[`${type}_ui_schema`]}
              formData={data}
              mode={
                action === "download"
                  ? BLOCK_TYPE_MODES.download
                  : BLOCK_TYPE_MODES.print
              }
            />,
          )}
        {noteCodes && (
          <>
            <hr style={{ borderTop: "1px solid #adccff" }} />
            <ClientServiceAndDiagnosis value={noteCodes} readOnly={true} />
          </>
        )}
        {metaData && <PrintFooter metaData={metaData} />}
      </Container>
    </div>
  );
};

export default PrintLayout;
